import { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import { useEffect } from 'react'
import { ICompanyBase, Language } from '@/types'
import { getCompanyLogo, getStaticStores, useStores } from '@/utils'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { MyHead } from '@/components/molecules/MyHead'
import { CompanyDetailHeader } from '@/components/organisms/CompanyDetailHeader'
import { PrimaryHeading } from '@/components/atoms/PrimaryHeading'
import { CompanyReferencesGroup } from '@/components/organisms/CompanyReferencesGroup'
import { ProfileSingleColumnContentTemplate } from '@/components/templates/ProfileSingleColumnContentTemplate'
import styles from './index.module.scss'

type Props = {
  company: ICompanyBase
}

const ReferencesOfCompany: NextPage<Props> = observer(({ company }) => {
  const { t } = useTranslation(['p'])
  const title = t('の応援コメント', { name: company.name })
  const { companies, viewer } = useStores()
  const companyInstance = companies.createCompanyInstance(company)
  let description = ''
  if (company?.aboutUs) {
    const descriptionPrefix = company?.aboutUs.substring(0, 130)
    const descriptionSuffix = company?.aboutUs.length > 130 ? '...' : ''
    description = `${descriptionPrefix}${descriptionSuffix}`
  }

  useEffect(() => {
    if (!company || !companyInstance) {
      return
    }

    if (viewer.isInitialized && viewer.isSignedIn) {
      companyInstance.fetch()
    }
  }, [viewer.isInitialized, company])

  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead title={title} description={description} image={getCompanyLogo(company, true)} />
      <ProfileSingleColumnContentTemplate header={<CompanyDetailHeader company={companyInstance} />} hasHeaderImage>
        <div className={styles.heading}>
          <PrimaryHeading backgroundText='References'>{title}</PrimaryHeading>
        </div>
        <div className={styles.content}>
          <CompanyReferencesGroup company={company} />
        </div>
      </ProfileSingleColumnContentTemplate>
    </VerticalNavDefaultPageTemplate>
  )
})

export const getServerSideProps: GetServerSideProps = async ({ locale, params }) => {
  const { companies, viewer } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const companySlug = params.id as string
  const company = await companies.fetchCompany({ slug: companySlug, limitOfJob: 0, limitOfReference: 100 })

  if (!company) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      company,
      ...(await serverSideTranslations(locale, [
        'global',
        'companies-users-[id]',
        'companies-[id]',
        'p',
        'validation',
      ])),
    },
  }
}

export default ReferencesOfCompany
